import { IkeAngle } from '../unit/angle';
import { linePlaneIntersection } from './geometry/geometry.util';
import { Vector } from './vector';

/** Capture pointing in a direction which is casting an infinite laser. */
export interface RayCapture {
	orientation: {
		bearing: number;
		pitch: number;
	};
	raw?: {
		orientation?: {
			yaw?: number;
		};
	};
}

/**  Capture pointing in a direction with a laser distance. */
export interface PointCapture extends RayCapture {
	distance: number;
}

export interface PointToPointMeasurement {
	horizontal: number;
	vertical: number;
	distance: number;
	bearing: number;
	pitch: number;
}

export interface PointToPointSagPoints {
	/** Distance between start and end points (m) */
	straightLineDistance: number;
	/** Start point at the origin, always (0,0) (m) */
	start: Vector;
	/** End point (m) */
	end: Vector;
	/** Mid point (m) */
	mid: Vector;
}

export class MultiPoint {
	/**
	 * Projects a vector into the plane of a wire defined by two points, and translates the result into 2D.
	 *
	 * This function is used as the first step of calculating wire sag.
	 * Steps:
	 *  1. Compute the 3D plane defined by `startPoint` and `endPoint` (the two attachment points of the wire).
	 *    - The plane is perpendicular to ground (sag is defined by gravity).
	 *  2. Cast `ray` into the plane, to create a new vector `midPoint` where `ray` intersects the plane.
	 *  3. Translate `startPoint`, `endPoint`, and `midPoint` into 2D.
	 *
	 * @param startPoint 3D attachment point-a of the wire.
	 * @param endPoint 3D attachment point-b of the wire.
	 * @param ray Device orientation which defines midpoint when intersecting with plane.
	 * @returns Each point translated into 2D, with origin around `startPoint`.
	 */
	static getSagPoints2D(startPoint: PointCapture, endPoint: PointCapture, ray: RayCapture): PointToPointSagPoints {
		// No need to correct bearing because points are not plotted (relative angles stay the same)
		const startBearing = MultiPoint.getRelativeBearing(startPoint);
		const endBearing = MultiPoint.getRelativeBearing(endPoint);
		const rayBearing = MultiPoint.getRelativeBearing(ray);

		const vA = Vector.distanceBearingPitchToCartesian(startPoint.distance, startBearing, startPoint.orientation.pitch);
		const vB = Vector.distanceBearingPitchToCartesian(endPoint.distance, endBearing, endPoint.orientation.pitch);

		const verticalAxis = new Vector(0, 0, 1);
		const vBDelta = vB.subtract(vA);

		const planeNormal = vBDelta.cross(verticalAxis);

		const endVector = Vector.distanceBearingPitchToCartesian(1.0, rayBearing, ray.orientation.pitch);
		const lineVector = {
			start: new Vector(0, 0, 0),
			end: endVector,
		};

		const vC = linePlaneIntersection(lineVector, vA, planeNormal);
		const vCDelta = vC.subtract(vA);

		const endX = Math.sqrt(vBDelta.x * vBDelta.x + vBDelta.y * vBDelta.y);
		const endY = vBDelta.z;

		const midX = Math.sqrt(vCDelta.x * vCDelta.x + vCDelta.y * vCDelta.y);
		const midY = vCDelta.z;

		return {
			straightLineDistance: vBDelta.norm(),
			start: new Vector(0.0, 0.0, 0.0),
			end: new Vector(endX, endY, 0.0),
			mid: new Vector(midX, midY, 0.0),
		};
	}

	/**
	 *   Takes two ordered bearings and return the angle between.
	 *   For example for parameters 0, 90 returns 90
	 *   while parameters 90, 0 return -90
	 *   @param a bearing in radians
	 *   @param b other bearing in radians
	 *   @return signed angle between the two bearings radians
	 */
	static angleBetweenBearings(a: number, b: number): number {
		const av = new Vector(Math.sin(a), Math.cos(a), 0);
		const bv = new Vector(Math.sin(b), Math.cos(b), 0);
		const planeNormal = new Vector(0, 0, 1);
		let unsignedAngle = av.dot(bv);
		unsignedAngle = Math.acos(unsignedAngle);
		const cross = av.cross(bv);
		const sign = planeNormal.dot(cross);
		return sign < 0 ? unsignedAngle : -unsignedAngle;
	}

	/**
	 *   intended to optimize the gyroscope and magnetomerter based bearings, in point to point mode
	 *   to obtain the most accurate  point to point length, and azimuth
	 *   @param trueBearingOfPointOne true bearing (magnetometer based) for point A in radians
	 *   @param trueBearingOfPointTwo true bearing (magnetometer based) for point B in radians
	 *   @param relativeBearingOfPointOne relative bearing (gyroscope based) for point A in radians
	 *   @param relativeBearingOfPointTwo relative bearing (gyroscope based) for point B in radians
	 *   @return double array with two points for the resultant bearings after fusion
	 */
	static getCorrectedBearings(
		trueBearingOfPointOne: number,
		trueBearingOfPointTwo: number,
		relativeBearingOfPointOne: number,
		relativeBearingOfPointTwo: number
	) {
		//get most accurate angle between bearings
		const angleBetweenVectors = MultiPoint.angleBetweenBearings(relativeBearingOfPointOne, relativeBearingOfPointTwo);

		//construct naive orientation of the two bearing with respect to true north
		const one = trueBearingOfPointOne;
		const optimalPointTwoBearing = IkeAngle.normalize(trueBearingOfPointOne + angleBetweenVectors);

		// find the angular difference between trueBearingOfPointTwo and optimalPointTwoBearing
		// which assuming the gyros are perfect is the magnetic error, divide by 2 to average it
		const differential = MultiPoint.angleBetweenBearings(optimalPointTwoBearing, trueBearingOfPointTwo) / 2.0;

		// rotate by that average error to reduce error in the azimuth, while
		// using the gyros for the optimal angle between and hence vector length
		return {
			a: IkeAngle.normalize(one + differential),
			b: IkeAngle.normalize(optimalPointTwoBearing + differential),
		};
	}

	static getBearings(a: PointCapture, b: PointCapture) {
		if (
			a.raw == null ||
			b.raw == null ||
			a.raw.orientation == null ||
			b.raw.orientation == null ||
			a.raw.orientation.yaw == null ||
			b.raw.orientation.yaw == null
		) {
			return {
				a: a.orientation.bearing,
				b: b.orientation.bearing,
			};
		}

		return MultiPoint.getCorrectedBearings(
			a.orientation.bearing,
			b.orientation.bearing,
			a.raw.orientation.yaw,
			b.raw.orientation.yaw
		);
	}

	/**
	 * Get bearing value between 0 and 2Pi.
	 *
	 * Prefer to use `yaw` from gyroscope reading.
	 * Gyroscope is considered more accurate than `bearing` from magnetometer.
	 */
	static getRelativeBearing(a: RayCapture): number {
		if (a.raw == null || a.raw.orientation == null || a.raw.orientation.yaw == null) {
			return IkeAngle.normalize(a.orientation.bearing);
		}

		return IkeAngle.normalize(a.raw.orientation.yaw);
	}

	/**
	 * Calculate the distance, bearing and pitch from pointA to pointB
	 *
	 * returns
	 *   horizontal - Horizontal distance between points (Meters)
	 *   vertical - Vertical distance between points (Meters)
	 *   distance - Distance between points (Meters)
	 *   bearing - Offset from north (between 0  2PI) (Radians)
	 *   pitch - Offset from horizontal (Between -PI and PI)
	 */
	static PointToPoint(a: PointCapture, b: PointCapture): PointToPointMeasurement {
		const bearing = MultiPoint.getBearings(a, b);

		const vA = Vector.distanceBearingPitchToCartesian(a.distance, bearing.a, a.orientation.pitch);
		const vB = Vector.distanceBearingPitchToCartesian(b.distance, bearing.b, b.orientation.pitch);

		const vDiff = vB.subtract(vA);
		const comp = Vector.distanceBearingAndPitchFromCartesian(vDiff);

		const horizontal = Math.sqrt(vDiff.x * vDiff.x + vDiff.y * vDiff.y);
		const vertical = vDiff.z;

		return {
			horizontal,
			vertical,
			distance: comp.x,
			bearing: IkeAngle.normalize(comp.y),
			pitch: comp.z,
		};
	}

	static Points(points: PointCapture[]): PointToPointMeasurement {
		const [a, b] = points;
		return MultiPoint.PointToPoint(a, b);
	}
}
